import type { OwnershipFlags } from '@risksmart-app/trpc/src/routers/frontend/shared/ownership-filter-input';
import type {
  Action_Bool_Exp,
  Ancestor_Contributor_Bool_Exp,
  Assessment_Activity_Bool_Exp,
  Assessment_Bool_Exp,
  Control_Bool_Exp,
  Document_Bool_Exp,
  Indicator_Bool_Exp,
  Internal_Audit_Entity_Bool_Exp,
  Issue_Bool_Exp,
  Obligation_Bool_Exp,
  Risk_Bool_Exp,
  Third_Party_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';

/** Every flag off — the fail-closed set used when no user is resolved. */
const NO_OWNERSHIP: OwnershipFlags = {
  owner: false,
  contributor: false,
  groupOwner: false,
  groupContributor: false,
  inheritedOwner: false,
  inheritedContributor: false,
  inheritedGroupOwner: false,
  inheritedGroupContributor: false,
};

/**
 * Builds the per-entity ownership `bool_exp` fragments from the My Items 8-flag
 * model. Relocated here from `pages/dashboards/my-items/hooks/` in COI-691:
 * `src/hooks/queries/**` needs it for the ownership leg of the widened register
 * hooks, and a hook importing from `pages/` is backwards. The stateful
 * `useGetQueryVariables` hook stays where it was.
 */
export const getQueryVariables = (flags: OwnershipFlags, userId?: string) => {
  // Ownership is a user-scoped filter, so an unresolved `userId` must fail
  // closed. Every branch below constrains on `UserId: { _eq: userId }`, and
  // Apollo drops an `_eq: undefined` on the way to the wire — Hasura then reads
  // the remaining `UserId: {}` as "any user", widening "owned by me" into "has
  // any owner". Falling back to the all-off set leaves the empty `_or` that an
  // all-false flag set already produces, which Hasura evaluates as false.
  const {
    owner,
    contributor,
    groupOwner,
    groupContributor,
    inheritedOwner,
    inheritedContributor,
    inheritedGroupOwner,
    inheritedGroupContributor,
  } = userId ? flags : NO_OWNERSHIP;

  // The contributor edge can be direct (UserGroupId null) or via a group
  // (UserGroupId set) — `isNull` picks the variant.
  const getAncestorContributorsConditions = (
    contributorType: 'contributor' | 'owner',
    isNull: boolean
  ): Ancestor_Contributor_Bool_Exp => ({
    _and: [
      { UserId: { _eq: userId } },
      { ContributorType: { _eq: contributorType } },
      { UserGroupId: { _is_null: isNull } },
    ],
  });

  const itemFilterConditions: Risk_Bool_Exp = { _or: [] };
  // assessment activities are only filtered by owners and ownerGroups
  const assessmentActivityFilterConditions: Assessment_Activity_Bool_Exp = {
    _or: [],
  };

  if (owner) {
    itemFilterConditions._or?.push({
      owners: { UserId: { _eq: userId } },
    });

    assessmentActivityFilterConditions._or?.push({
      owners: { UserId: { _eq: userId } },
    });
  }

  if (contributor) {
    itemFilterConditions._or?.push({
      contributors: { UserId: { _eq: userId } },
    });
  }

  if (groupOwner) {
    itemFilterConditions._or?.push({
      ownerGroups: {
        group: { users: { UserId: { _eq: userId } } },
      },
    });
    assessmentActivityFilterConditions._or?.push({
      ownerGroups: {
        group: { users: { UserId: { _eq: userId } } },
      },
    });
  }

  if (groupContributor) {
    itemFilterConditions._or?.push({
      contributorGroups: {
        group: { users: { UserId: { _eq: userId } } },
      },
    });
  }

  if (inheritedOwner) {
    itemFilterConditions._or?.push({
      _and: [
        { _not: { owners: { UserId: { _eq: userId } } } },
        {
          ancestorContributors: getAncestorContributorsConditions(
            'owner',
            true
          ),
        },
      ],
    });
  }

  if (inheritedGroupOwner) {
    itemFilterConditions._or?.push({
      _and: [
        {
          _not: {
            ownerGroups: {
              group: { users: { UserId: { _eq: userId } } },
            },
          },
        },
        {
          ancestorContributors: getAncestorContributorsConditions(
            'owner',
            false
          ),
        },
      ],
    });
  }

  if (inheritedContributor) {
    itemFilterConditions._or?.push({
      _and: [
        {
          _not: { contributors: { UserId: { _eq: userId } } },
        },
        {
          ancestorContributors: getAncestorContributorsConditions(
            'contributor',
            true
          ),
        },
      ],
    });
  }

  if (inheritedGroupContributor) {
    itemFilterConditions._or?.push({
      _and: [
        {
          _not: {
            contributorGroups: {
              group: { users: { UserId: { _eq: userId } } },
            },
          },
        },
        {
          ancestorContributors: getAncestorContributorsConditions(
            'contributor',
            false
          ),
        },
      ],
    });
  }

  return {
    userId: userId ?? '',
    riskFilterConditions: itemFilterConditions,
    actionFilterConditions: itemFilterConditions as Action_Bool_Exp,
    indicatorFilterConditions: itemFilterConditions as Indicator_Bool_Exp,
    documentFilterConditions: itemFilterConditions as Document_Bool_Exp,
    assessmentFilterConditions: itemFilterConditions as Assessment_Bool_Exp,
    issueFilterConditions: itemFilterConditions as Issue_Bool_Exp,
    internalAuditFilterConditions:
      itemFilterConditions as Internal_Audit_Entity_Bool_Exp,
    obligationFilterConditions: itemFilterConditions as Obligation_Bool_Exp,
    thirdPartyFilterConditions: itemFilterConditions as Third_Party_Bool_Exp,
    controlFilterConditions: itemFilterConditions as Control_Bool_Exp,
    assessmentActivityFilterConditions,
  };
};
